import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { downloadPdf, downloadExcel } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileDown, FileSpreadsheet, Landmark, Loader2 } from "lucide-react";

const COLS = [["ht", "CA HT (base)"], ["tva", "TVA collectée"], ["base_timbre", "Base timbre"], ["timbre", "Droit de timbre"], ["ttc", "Total TTC"]];

const slug = (n) => (n || "").replace(/\s+/g, "-");
const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const pdfG50 = (id, name, annee, mois) => downloadPdf(`/pdf/g50?annee=${annee}&societe_id=${id}${mois ? `&mois=${mois}` : ""}`,
  `etat-g50-${slug(name)}-${annee}${mois ? `-${String(mois).padStart(2, "0")}` : ""}`);

const excelG50 = (id, name, annee, mois) => downloadExcel(`/pdf/g50/excel?annee=${annee}&societe_id=${id}${mois ? `&mois=${mois}` : ""}`,
  `etat-g50-${slug(name)}-${annee}${mois ? `-${String(mois).padStart(2, "0")}` : ""}`);

const SocieteG50 = ({ s, annee, mois }) => (
  <div className="mb-5 last:mb-0" data-testid={`g50-societe-${s.societe_id}`}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm font-semibold mr-auto">{s.societe}</p>
      <Button variant="outline" size="sm" className="gap-2" onClick={() => pdfG50(s.societe_id, s.societe, annee, mois)} data-testid={`g50-pdf-societe-${s.societe_id}`}>
        <FileDown className="h-4 w-4" /> PDF {s.societe} {mois ? `${MOIS[mois - 1]} ` : ""}{annee}
      </Button>
      <Button variant="outline" size="sm" className="gap-2 ml-2" onClick={() => excelG50(s.societe_id, s.societe, annee, mois)} data-testid={`g50-excel-societe-${s.societe_id}`}>
        <FileSpreadsheet className="h-4 w-4" /> Excel
      </Button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="text-left text-muted-foreground border-b">
          <th className="py-2 pr-2 font-medium">Mois</th>
          <th className="py-2 pr-2 font-medium text-right">Factures</th>
          <th className="py-2 pr-2 font-medium text-right">Taux</th>
          {COLS.map(([k, h]) => <th key={k} className="py-2 pr-2 font-medium text-right">{h}</th>)}
        </tr></thead>
        <tbody className="divide-y">
          {s.rows.map((r) => (
            <tr key={r.num} className={r.nb ? "" : "text-muted-foreground"}>
              <td className="py-2 pr-2">{r.mois}</td>
              <td className="py-2 pr-2 tabular text-right">{r.nb}</td>
              <td className="py-2 pr-2 text-right">{r.taux || "—"}</td>
              {COLS.map(([k]) => <td key={k} className="py-2 pr-2 tabular text-right">{fmtDA(r[k])}</td>)}
            </tr>
          ))}
          <tr className="font-bold border-t-2 border-primary/30" data-testid={`g50-total-${s.societe_id}`}>
            <td className="py-2">TOTAL</td>
            <td className="py-2 pr-2 tabular text-right">{s.rows.reduce((a, r) => a + r.nb, 0)}</td>
            <td />
            {COLS.map(([k]) => <td key={k} className="py-2 pr-2 tabular text-right">{fmtDA(s.rows.reduce((a, r) => a + r[k], 0))}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export const G50Card = ({ societeId, exercice }) => {
  const [data, setData] = useState(null);
  const [mois, setMois] = useState(0);
  const { societes } = useApp();
  const socs = societes.filter((s) => (data || []).some((d) => d.societe_id === s.id));
  const annee = exercice === "all" ? new Date().getFullYear() : exercice;
  const params = { annee, ...(societeId !== "all" && { societe_id: societeId }), ...(mois && { mois }) };

  useEffect(() => {
    setData(null);
    api.get("/pdf/g50/data", { params }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [societeId, exercice, mois]); // eslint-disable-line

  return (
    <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-g50">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 mr-auto">
          <Landmark className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">TVA collectée &amp; timbre mensuels — G50 <span className="text-sm font-normal text-muted-foreground">({annee})</span></h3>
        </div>
        <Button variant="outline" size="sm" className="gap-2" data-testid="g50-pdf-btn"
          onClick={() => downloadPdf(`/pdf/g50?${new URLSearchParams(params).toString()}`, `etat-g50-${annee}`)}>
          <FileDown className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" size="sm" className="gap-2" data-testid="g50-excel-btn"
          onClick={() => downloadExcel(`/pdf/g50/excel?${new URLSearchParams(params).toString()}`, `etat-g50-${annee}`)}>
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-muted-foreground">Mois :</span>
        <select value={mois} onChange={(e) => setMois(Number(e.target.value))} data-testid="g50-mois-filter"
          className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value={0}>Tous les mois</option>
          {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-2">PDF par société :</span>
        {[2025, 2026].flatMap((y) => socs.map((s) => (
          <Button key={`${s.id}-${y}`} size="sm" variant="ghost" className="gap-1" onClick={() => pdfG50(s.id, s.name, y, mois)} data-testid={`g50-pdf-${y}-${s.id}`}>
            <FileDown className="h-4 w-4" /> {s.name} {mois ? `${MOIS[mois - 1]} ` : ""}{y}
          </Button>
        )))}
      </div>
      {data === null
        ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : data.map((s) => <SocieteG50 key={s.societe_id} s={s} annee={annee} mois={mois} />)}
    </div>
  );
};
